import { Language } from "@shared/locales";
import axios, { AxiosInstance, HttpStatusCode, isAxiosError } from "axios";
import { Client, ClientApiException, IProblemDetails } from "../client/client.generated";
import { translate } from "../locales/translator";

const timeoutErrorMessage = "Door een timeout zijn er geen gegevens";
export abstract class ClientService {
  private static errorMessageFactories = new Map<HttpStatusCode, ErrorMessageFactory>([
    [HttpStatusCode.BadRequest, (data: IProblemDetails) => data?.title],
    [HttpStatusCode.NotFound, (data: IProblemDetails) => data?.title],
    [HttpStatusCode.RequestTimeout, () => timeoutErrorMessage],
    [HttpStatusCode.BadGateway, () => timeoutErrorMessage],
    [
      HttpStatusCode.InternalServerError,
      (data: IProblemDetails) => {
        return data?.status &&
          [HttpStatusCode.RequestTimeout, HttpStatusCode.BadGateway].includes(data.status)
          ? timeoutErrorMessage
          : data?.title;
      }
    ]
  ]);

  static getErrorData = <T = IProblemDetails, E extends Error = Error>(error: E): T | undefined => {
    // TODO: response type from ClientApiException is not generic and could be wrong because it thinks it's a string
    if (ClientApiException.isClientApiException(error) && error.response)
      return error.response as unknown as T;
    if (isAxiosError<T>(error) && error.response) return error.response.data;
  };

  static getStatusCode = <E extends Error = Error>(error: E): HttpStatusCode | undefined => {
    if (ClientApiException.isClientApiException(error)) return error.status;
    if (isAxiosError(error) && error.response) return error.response.status;
  };

  static getErrorMessage = <T = IProblemDetails, E extends Error = Error>(error: E): string => {
    const status = this.getStatusCode(error);
    const errorMessageFactory = this.getErrorMessageFactory(status);

    const data = this.getErrorData<T>(error);
    return errorMessageFactory(data) ?? translate("ErGingIetsMis");
  };

  private static getErrorMessageFactory = (status?: HttpStatusCode): ErrorMessageFactory => {
    const defaultErrorMessageFactory = () => translate("ErGingIetsMis");
    const errorMessageFactory = status ? this.errorMessageFactories.get(status) : undefined;

    return errorMessageFactory ?? defaultErrorMessageFactory;
  };

  protected readonly client: Client;

  constructor(protected readonly baseUrl: string, protected readonly instance: AxiosInstance = axios) {
    this.client = new Client(baseUrl, instance);
  }

  protected readonly bind = <T extends Function>(method: T) => method.bind(this.client) as T;

  protected readonly createRequestMethods = <T extends Array<any>, R>(
    methods: Record<Language, RequestMethod<T, R>>
  ): RequestMethods<T, R> => {
    return Object.entries(methods).reduce((requests, [language, method]) => {
      return Object.assign(requests, { [language]: this.bind(method) });
    }, {} as RequestMethods<T, R>);
  };

  protected readonly invokeRequestMethod = <T extends Array<any>, R>(
    requestMethod: RequestMethod<T, R>,
    ...args: T
  ): Promise<R> => {
    this.requestMethodGuard(requestMethod);

    return requestMethod(...args);
  };

  protected readonly requestMethodGuard = <T extends Array<any>, R>(method: RequestMethod<T, R>): void => {
    if (method === null || method === undefined) {
      throw new Error("Request method not found");
    }
  };
}

export type RequestMethods<T extends Array<any>, R> = Record<Language, (...args: T) => Promise<R>>;
export type RequestMethod<T extends Array<any>, R> = RequestMethods<T, R>[keyof RequestMethods<T, R>];

export type ErrorMessageFactory = (
  data?: ReturnType<typeof ClientService.getErrorData>
) => string | null | undefined;
